import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { fetchArchdeaconryBySlug } from '@/services/api';
import { 
  Church, 
  Users, 
  MapPin, 
  ChevronRight, 
  ArrowLeft, 
  Info,
  ShieldCheck
} from 'lucide-react';
import ImageHelper from '../lib/imageHelper';

interface Clergy {
    id: number;
    name: string;
    title: string;
    slug: string;
    photo?: string;
}

interface Parish {
    id: number;
    name: string;
    slug: string;
    location: string;
}

interface Archdeaconry {
    id: number;
    name: string;
    slug: string;
    parishes: Parish[];
    priests: Clergy[];
    description: string;
    current_archdeacon?: Clergy;
    current_supervisor?: Clergy;
}

const getInitials = (name: string) =>
    name?.split(' ').map((n) => n[0]).join('').toUpperCase();

const ArchdeaconryProfile = () => {
    const { slug } = useParams<{ slug: string }>();
    const { toast } = useToast();
    const [archdeaconry, setArchdeaconry] = useState<Archdeaconry | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadArchdeaconry = async () => {
            try {
                const response = await fetchArchdeaconryBySlug(slug!);
                setArchdeaconry(response.data);
            } catch (error) {
                toast({
                    title: 'Error',
                    description: 'Failed to fetch archdeaconry details.',
                    variant: 'destructive',
                });
            } finally {
                setLoading(false);
            }
        };
        loadArchdeaconry();
    }, [slug, toast]);

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
            <div className="flex flex-col items-center gap-4">
                <div className="h-12 w-12 border-4 border-blue-600/20 border-t-blue-600 rounded-full animate-spin"></div>
                <p className="text-slate-500 font-medium italic">Loading Archdeaconry...</p>
            </div>
        </div>
    );

    if (!archdeaconry) return <div className="p-20 text-center text-red-500 font-serif text-xl">Archdeaconry not found.</div>;

    const leader = archdeaconry.current_archdeacon || archdeaconry.current_supervisor;
    const leaderRole = archdeaconry.current_archdeacon ? 'Archdeacon' : 'Supervisor';

    return (
        <div className="min-h-screen bg-slate-50 pb-20">
            {/* Header / Hero */}
            <div className="bg-slate-900 text-white pt-16 pb-32 px-4 relative overflow-hidden">
                <div className="absolute inset-0 opacity-10 pointer-events-none">
                     <Church className="absolute -right-10 -bottom-10 h-64 w-64 rotate-12" />
                </div>
                
                <div className="max-w-6xl mx-auto relative z-10">
                    <Link to="/archdeaconries" className="inline-flex items-center text-blue-300 hover:text-white mb-6 transition-colors group">
                        <ArrowLeft className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" />
                        Back to Archdeaconries
                    </Link>
                    <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4">{archdeaconry.name}</h1>
                    <div className="flex items-center gap-2 text-slate-400">
                        <MapPin className="h-4 w-4" />
                        <span className="uppercase tracking-widest text-xs font-bold">Ibadan South Diocese</span>
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 -mt-16 relative z-20">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    
                    {/* LEFT COLUMN: Leader & Description */}
                    <div className="lg:col-span-4 space-y-6">
                        {leader && (
                            <Card className="border-none shadow-xl rounded-3xl overflow-hidden ring-1 ring-slate-200">
                                <div className="bg-blue-900 p-4 flex items-center justify-between text-white">
                                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">{leaderRole}</span>
                                    <ShieldCheck className="h-4 w-4 text-blue-300" />
                                </div>
                                <CardContent className="p-6 text-center">
                                    <div className="mx-auto w-24 h-24 mb-4 ring-4 ring-slate-50 rounded-full overflow-hidden shadow-inner bg-slate-100 flex items-center justify-center">
                                        {leader.photo ? (
                                            <ImageHelper src={leader.photo} alt={leader.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <span className="text-2xl font-bold text-slate-400">{getInitials(leader.name)}</span>
                                        )}
                                    </div>
                                    <h2 className="text-xl font-serif font-bold text-slate-900 leading-tight mb-1">
                                        {leader.title} {leader.name}
                                    </h2>
                                    <Link 
                                        to={`/clergy/${leader.slug}`} 
                                        className="text-sm text-blue-600 hover:underline font-medium inline-flex items-center mt-2"
                                    >
                                        View Profile <ChevronRight className="h-3 w-3 ml-1" />
                                    </Link>
                                </CardContent>
                            </Card>
                        )}

                        <Card className="border-none shadow-sm rounded-3xl bg-white/80 backdrop-blur-sm">
                            <CardContent className="p-6">
                                <div className="flex items-center gap-2 mb-4 text-slate-800">
                                    <Info className="h-4 w-4 text-blue-600" />
                                    <h3 className="font-bold uppercase text-xs tracking-widest">About the Territory</h3>
                                </div>
                                <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-line font-serif italic">
                                    {archdeaconry.description || "The history and administrative profile of this Archdeaconry is currently being updated by the Diocese."}
                                </p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* RIGHT COLUMN: Lists */}
                    <div className="lg:col-span-8 space-y-8">
                        
                        {/* Parishes Section */}
                        <div className="bg-white rounded-[2rem] shadow-sm border border-slate-100 overflow-hidden">
                            <div className="p-8 border-b border-slate-50 flex items-center justify-between bg-slate-50/50">
                                <div className="flex items-center gap-3">
                                    <div className="bg-blue-100 p-2 rounded-xl text-blue-700">
                                        <Church className="h-5 w-5" />
                                    </div>
                                    <h3 className="text-lg font-serif font-bold text-slate-800">Constituent Parishes</h3>
                                </div>
                                <span className="bg-white px-3 py-1 rounded-full text-xs font-bold text-slate-500 shadow-sm ring-1 ring-slate-200">
                                    {archdeaconry.parishes.length} Churches
                                </span>
                            </div>
                            
                            <div className="p-2">
                                {archdeaconry.parishes.length === 0 ? (
                                    <div className="p-10 text-center text-slate-400 italic">No parishes listed.</div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        {archdeaconry.parishes.map((parish) => (
                                            <Link 
                                                key={parish.id} 
                                                to={`/parishes/${parish.slug}`}
                                                className="flex items-center justify-between p-4 rounded-2xl hover:bg-blue-50 transition-colors group"
                                            >
                                                <div className="flex flex-col">
                                                    <span className="font-bold text-slate-900 group-hover:text-blue-700 transition-colors">{parish.name}</span>
                                                    <span className="text-xs text-slate-500 flex items-center mt-1 uppercase tracking-tighter">
                                                        <MapPin className="h-3 w-3 mr-1 opacity-50" /> {parish.location}
                                                    </span>
                                                </div>
                                                <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-blue-400 transition-all" />
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Clergy Section */}
                        <div className="bg-white rounded-[2rem] shadow-sm border border-slate-100 overflow-hidden">
                            <div className="p-8 border-b border-slate-50 flex items-center gap-3 bg-slate-50/50">
                                <div className="bg-amber-100 p-2 rounded-xl text-amber-700">
                                    <Users className="h-5 w-5" />
                                </div>
                                <h3 className="text-lg font-serif font-bold text-slate-800">Assigned Clergy</h3>
                            </div>
                            
                            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {archdeaconry.priests.length === 0 ? (
                                    <div className="col-span-full p-10 text-center text-slate-400 italic">No clergy listed.</div>
                                ) : (
                                    archdeaconry.priests.map((clergy) => (
                                        <Link 
                                            key={clergy.id} 
                                            to={`/clergy/${clergy.slug}`}
                                            className="bg-slate-50/50 border border-slate-100 p-4 rounded-2xl flex items-center gap-3 hover:shadow-md hover:bg-white transition-all group"
                                        >
                                            <div className="h-10 w-10 shrink-0 rounded-full overflow-hidden bg-white ring-2 ring-white">
                                                {clergy.photo ? (
                                                    <ImageHelper src={clergy.photo} alt={clergy.name} className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center text-[10px] font-bold text-slate-400">
                                                        {getInitials(clergy.name)}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-xs font-bold text-slate-400 uppercase tracking-tighter">{clergy.title}</p>
                                                <p className="text-sm font-bold text-slate-800 truncate group-hover:text-blue-600 transition-colors">{clergy.name}</p>
                                            </div>
                                        </Link>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default ArchdeaconryProfile;